/**
 * @file planTypeDisplayMode.js
 * @description Constants and helpers for ClientPlanType.displayMode / planTypeDisplayToId.
 */

import { getText } from '../../../i18n';

export const DISPLAY_MODE = {
    VISIBLE_TO_ALL: 'VisibleToAll',
    DO_NOT_DISPLAY: 'DoNotDisplay',
    ENROLLMENT_ONLY: 'EnrollmentOnly',
    POPULATION: 'Population',
};

export const DISPLAY_MODE_OPTIONS = [
    { value: DISPLAY_MODE.VISIBLE_TO_ALL, label: getText('msg_e89b51317152') },
    { value: DISPLAY_MODE.DO_NOT_DISPLAY, label: getText('msg_dc406af1aed7') },
    { value: DISPLAY_MODE.ENROLLMENT_ONLY, label: getText('msg_cbcac10dd6a5') },
    { value: DISPLAY_MODE.POPULATION, label: getText('msg_eb8f3ce2a98e') },
];

/**
 * Backend planTypeDisplayToId values:
 *   1 => Do Not Display
 *   2 => Enrollment Only
 *   3 => Population Group
 *   4 => Visible to All
 */
const DISPLAY_TO_ID_MAP = {
    1: DISPLAY_MODE.DO_NOT_DISPLAY,
    2: DISPLAY_MODE.ENROLLMENT_ONLY,
    3: DISPLAY_MODE.POPULATION,
    4: DISPLAY_MODE.VISIBLE_TO_ALL,
};

const DISPLAY_MODE_TO_ID_MAP = {
    [DISPLAY_MODE.DO_NOT_DISPLAY]: 1,
    [DISPLAY_MODE.ENROLLMENT_ONLY]: 2,
    [DISPLAY_MODE.POPULATION]: 3,
    [DISPLAY_MODE.VISIBLE_TO_ALL]: 4,
};

/**
 * Convert a backend planTypeDisplayToId integer to a frontend displayMode string.
 */
export function displayToIdToMode(planTypeDisplayToId) {
    return (
        DISPLAY_TO_ID_MAP[planTypeDisplayToId] || DISPLAY_MODE.ENROLLMENT_ONLY
    );
}

function normalizeDisplayText(displayText) {
    return String(displayText ?? '')
        .trim()
        .toLowerCase();
}

/**
 * Convert a frontend displayMode string to a backend planTypeDisplayToId integer.
 */
export function displayModeToId(displayMode) {
    return DISPLAY_MODE_TO_ID_MAP[displayMode] || 4;
}

/**
 * Pick a display mode out of a ClientPlanType view-model, supporting both
 * the new `planTypeDisplayToId` integer and legacy `displayMode` string.
 */
export function resolveDisplayMode(viewModel) {
    if (viewModel?.planTypeDisplayToId) {
        return displayToIdToMode(Number(viewModel.planTypeDisplayToId));
    }

    const displayText = normalizeDisplayText(
        viewModel?.planTypeDisplayTo ?? viewModel?.displayMode,
    );

    if (displayText === 'visible to all' || displayText === 'display to all') {
        return DISPLAY_MODE.VISIBLE_TO_ALL;
    }

    if (displayText === 'do not display' || displayText === 'donotdisplay') {
        return DISPLAY_MODE.DO_NOT_DISPLAY;
    }

    if (displayText === 'enrollment only' || displayText === 'enrollmentonly') {
        return DISPLAY_MODE.ENROLLMENT_ONLY;
    }

    if (displayText === 'population group' || displayText === 'population') {
        return DISPLAY_MODE.POPULATION;
    }

    if (viewModel?.displayMode) return viewModel.displayMode;
    return DISPLAY_MODE.ENROLLMENT_ONLY;
}

/**
 * Map a display-mode value to a /branding pill class + label.
 * See temp/Benefits_Module/ADMIN_REDESIGN_PLAN.md §4.2.
 */
export function getDisplayModePill(mode) {
    switch (mode) {
        case DISPLAY_MODE.VISIBLE_TO_ALL:
            return {
                className: 'ing-badge-completed',
                label: getText('msg_062b8e43b3c4'),
            };
        case DISPLAY_MODE.ENROLLMENT_ONLY:
            return { className: 'ing-badge-active', label: getText('msg_cbcac10dd6a5') };
        case DISPLAY_MODE.POPULATION:
            return { className: 'ing-badge-template', label: getText('msg_182d37ec74bf') };
        case DISPLAY_MODE.DO_NOT_DISPLAY:
        default:
            return { className: 'ing-badge-inactive', label: getText('msg_d4c2792a7245') };
    }
}

/**
 * Backwards-compatible derived flag — keep the legacy `isActive` aligned
 * with the new enum so older consumers still work.
 */
export function deriveIsActive(displayMode) {
    return displayMode !== DISPLAY_MODE.DO_NOT_DISPLAY;
}

/**
 * Effective sort order for a ClientPlanType row.
 *
 * The API can return `ClientPlanType.sortOrder` as 0 for rows that were
 * never rearranged, but the meaningful order still lives on the nested
 * `planType.sortOrder` (Medical=10, Tobacco=12, Spouse=15, Dental=20, …).
 * A non-zero `ClientPlanType.sortOrder` (set via Arrange Plan Types) wins
 * so an admin override beats the system default.
 */
export function getEffectivePlanTypeSortOrder(pt) {
    const own = Number(pt?.sortOrder) || 0;
    if (own > 0) return own;
    return Number(pt?.planType?.sortOrder) || 0;
}
